/**
 * Reading and writing the free-form JSON every SyncAudit record carries.
 *
 * `extra_values` is a text column on customer, spec_sheet, ball_drilled,
 * thumb_cut and proshop. The entity's setter json_encodes whatever it is
 * given and the getter json_decodes once, so a value that arrived already
 * encoded comes back as a string of JSON -- sometimes twice over. That is why
 * reading it means unwrapping until an object falls out, rather than a single
 * JSON.parse.
 *
 * The same field is already occupied: a bowler's WooCommerce link lives in
 * `pos`, a ball's insert colours and barcode in their own keys. So it is
 * always merged, never assigned -- overwriting a customer's extra_values
 * would silently unlink them from their POS customer.
 *
 * (There are three private copies of the unwrapping loop in the ball and
 * plugin code, written before this file existed. They behave identically;
 * this is where a new caller should come.)
 */

export const parseExtraValues = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};
  let current = raw;
  // Bounded rather than while(true): a value that never resolves to an object
  // must not hang the render it is being read in.
  for (let i = 0; i < 20; i++) {
    try {
      const next = JSON.parse(current);
      if (next && typeof next === "object") return next;
      if (typeof next === "string") {
        current = next;
        continue;
      }
      return {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

export const mergeExtraValues = (existing, patch) => ({
  ...parseExtraValues(existing),
  ...patch,
});

/**
 * Whether this record was created by reading a scanned sheet.
 *
 * The only durable trace there is. The scanned page is attached as
 * "<sheetId>-scan.png", but a scan can finish without a page, attaching is
 * deliberately non-fatal, and deleting the attachment would take the evidence
 * with it.
 */
export const scannedFrom = (extraValues) => {
  const scan = parseExtraValues(extraValues).scan;
  return scan && typeof scan === "object" ? scan : null;
};
